/**
 * shared/types.ts — the core data contracts shared by sim (server + worker) and client.
 *
 * OWNER: integration (contract). Other agents: request changes in docs/CONTRACT-REQUESTS.md.
 * Pure TS: no DOM, no Node.  Space: CoD Z-up inches (see shared/math.ts header).
 *
 * Naming follows the Q3/IW engine (ps.pm_flags, cmd.forwardmove, ...) so that the decompiled
 * pseudocode in research/pmove-t6mp.md maps 1:1 onto our code.
 *
 * Enums are `as const` objects + union types (no TS `enum`) so every runtime (tsx, esbuild,
 * vite, a Worker) treats them identically.
 */
import type { Vec3 } from './math';

// =============================================================================================
// Weapons
// =============================================================================================
/**
 * Weapon ids for the SnS set [research/weapons-mp.md §1]. Index into ps.ammoClip / ps.ammoStock.
 * The numeric values are part of the wire format; append only.
 */
export const WeaponId = {
  NONE: 0,
  CROSSBOW: 1, //          crossbow_mp          primary + spawn weapon, clip 3 stock 3
  EXPLOSIVE_BOLT: 2, //    explosive_bolt_mp    the stuck bolt (grenade weapon of the crossbow), never held
  KNIFE_BALLISTIC: 3, //   knife_ballistic_mp   secondary, clip 1 stock 2, useAsMelee
  HATCHET: 4, //           hatchet_mp           lethal offhand (+frag), clip 1 (max 4)
  KNIFE: 5, //             knife_mp             melee weapon (crossbow melee uses this def)
} as const;
export type WeaponId = (typeof WeaponId)[keyof typeof WeaponId];
export const NUM_WEAPONS = 6;
/** Asset/weapon-file names, indexed by WeaponId. */
export const WEAPON_NAMES: readonly string[] = ['none', 'crossbow_mp', 'explosive_bolt_mp', 'knife_ballistic_mp', 'hatchet_mp', 'knife_mp'];
export function weaponIdFromName(name: string): WeaponId {
  const i = WEAPON_NAMES.indexOf(name);
  return (i < 0 ? 0 : i) as WeaponId;
}

/**
 * Weapon state machine states (IW3 weaponstate_t names; numeric values are ours, NOT T6's —
 * pmove-t6mp.md §3 mentions T6 states 0x38..0x3a ending sprint, which we cannot map yet).
 * Owned semantically by the weapons agent; append-only.
 */
export const WeaponState = {
  READY: 0,
  RAISING: 1,
  RAISING_ALTSWITCH: 2,
  DROPPING: 3,
  DROPPING_QUICK: 4,
  FIRING: 5,
  RECHAMBERING: 6,
  RELOADING: 7,
  RELOADING_INTERUPT: 8,
  RELOAD_START: 9,
  RELOAD_START_INTERUPT: 10,
  RELOAD_END: 11,
  MELEE_INIT: 12,
  MELEE_FIRE: 13,
  MELEE_END: 14,
  OFFHAND_INIT: 15,
  OFFHAND_PREPARE: 16,
  OFFHAND_HOLD: 17,
  OFFHAND_START: 18,
  OFFHAND: 19,
  OFFHAND_END: 20,
  SPRINT_RAISE: 21,
  SPRINT_LOOP: 22,
  SPRINT_DROP: 23,
  DTP_IN: 24,
  DTP_LOOP: 25,
  DTP_OUT: 26,
  FIRST_RAISE: 27,
} as const;
export type WeaponState = (typeof WeaponState)[keyof typeof WeaponState];

/**
 * Viewmodel animation ids = the weapon-file anim slots (game/public/assets/anims/vm_*.json `slots`).
 * ps.weaponAnim = id | (toggle ? WEAPANIM_TOGGLEBIT : 0); the toggle bit flips each time the same
 * anim restarts (Q3 ANIM_TOGGLEBIT pattern) so the viewmodel can detect restarts.
 */
export const WeapAnim = {
  IDLE: 0, EMPTY_IDLE: 1, FIRE: 2, LAST_SHOT: 3, HOLD_FIRE: 4, RELOAD: 5, RELOAD_EMPTY: 6,
  RAISE: 7, DROP: 8, FIRST_RAISE: 9, ALT_RAISE: 10, ALT_DROP: 11, QUICK_RAISE: 12, QUICK_DROP: 13,
  EMPTY_RAISE: 14, EMPTY_DROP: 15, SPRINT_IN: 16, SPRINT_LOOP: 17, SPRINT_OUT: 18,
  SPRINT_IN_EMPTY: 19, SPRINT_LOOP_EMPTY: 20, SPRINT_OUT_EMPTY: 21,
  CRAWL_IN: 22, CRAWL_FORWARD: 23, CRAWL_BACK: 24, CRAWL_RIGHT: 25, CRAWL_LEFT: 26, CRAWL_OUT: 27,
  ADS_FIRE: 28, ADS_LAST_SHOT: 29, ADS_UP: 30, ADS_DOWN: 31,
  MELEE: 32, MELEE_CHARGE: 33, DTP_IN: 34, DTP_LOOP: 35, DTP_OUT: 36,
} as const;
export type WeapAnim = (typeof WeapAnim)[keyof typeof WeapAnim];
export const WEAPANIM_TOGGLEBIT = 0x100;

// =============================================================================================
// Player movement enums
// =============================================================================================
/** Network/gameplay stance. NOTE: T6's PM_GetEffectiveStance returns 0 stand, 1 PRONE, 2 crouch
 *  internally [PM §3] — pmove may use that order privately; everything outside pmove uses this. */
export const Stance = { STAND: 0, CROUCH: 1, PRONE: 2 } as const;
export type Stance = (typeof Stance)[keyof typeof Stance];

/** pm_type. Values follow IW3 (T6 numbering not fully mapped, PM §1). */
export const PmType = {
  NORMAL: 0,
  NORMAL_LINKED: 1,
  NOCLIP: 2,
  UFO: 3,
  SPECTATOR: 4,
  INTERMISSION: 5,
  LASTSTAND: 6,
  DEAD: 7,
  DEAD_LINKED: 8,
  FREEZE: 9, // pre-match / end-of-match frozen: view rotates, no movement
} as const;
export type PmType = (typeof PmType)[keyof typeof PmType];

/** pm_flags bits [PM §1 table]. Names are behaviour guesses from the decompile. */
export const PMF = {
  PRONE: 0x1,
  DUCKED: 0x2,
  MANTLE: 0x4,
  LADDER: 0x8,
  UNK_0x10: 0x10, // ends sprint (PM_SprintEndCheck)
  BACKWARDS_RUN: 0x20,
  ADS_SLOW: 0x40, // "ADS-slow": x0.4 move scale in CmdScale
  TIME_SLOWDOWN: 0x80, // friction x0.3, accel x0.25
  TIME_KNOCKBACK: 0x100, // accel 1, gravity on ground
  FROZEN: 0x400,
  UNK_0x800: 0x800, // blocks clearing sprintButtonUpRequired
  LADDER_FALL: 0x2000,
  JUMPING: 0x4000,
  SPRINTING: 0x8000,
  VEHICLE_TURRET: 0x10000,
  SPECIAL_FRICTION: 0x20000,
  NO_SPRINT: 0x40000,
  NO_JUMP: 0x80000,
  DIVE: 0x200000, // dive-to-prone (dtp)
  UNK_0x400000: 0x400000,
  SLIDE: 0x40000000,
} as const;

/** eFlags (ours; shared by PlayerState and PlayerEntityState). */
export const EF = {
  DEAD: 0x1,
  CONNECTION_INTERRUPTED: 0x2, // EF_CONNECTION: no cmds for CONNECTION_INTERRUPTED_MS
  FIRING: 0x4,
  ADS: 0x8,
  SPRINTING: 0x10,
  MANTLING: 0x20,
  SPAWN_PROTECTED: 0x40,
  NO_SPRINT_LIMIT: 0x4000, // T6 eFlag 0x4000 -> sprint base 740 ms (PM §3 PM_SprintMaxDuration)
  TELEPORT_BIT: 0x8000, // toggled on teleport/spawn: never interpolate across (Q3 EF_TELEPORT_BIT)
} as const;

// =============================================================================================
// Usercmd  [NET §6.1, PM §1 "usercmd button bits"]
// =============================================================================================
/**
 * Button bits carry RESOLVED intents (input layer applies layouts, toggles, hold timers);
 * pmove never sees raw pad buttons. Edge detection (jump re-press, melee press, switch press)
 * is done in pmove by comparing with ps.oldButtons.
 */
export const Button = {
  ATTACK: 1 << 0, //        level: fire held (R2)
  ADS: 1 << 1, //           level: aim held, already = holdADS XOR toggledADS [CTL §6]
  MELEE: 1 << 2, //         level: melee held (edge = new swing)
  JUMP: 1 << 3, //          level: jump held (pmove requires re-press, PM_CheckJump)
  CROUCH: 1 << 4, //        level: desired stance is crouch (input resolves tap/hold, CTL §6)
  PRONE: 1 << 5, //         level: desired stance is prone (pressing while sprinting = dive-to-prone)
  SPRINT: 1 << 6, //        level: sprint requested (latched L3 tap)
  RELOAD: 1 << 7, //        level: reload (tap of +usereload)
  USE: 1 << 8, //           level: use held (pickup / hold □ >= USE_HOLD_MS)
  FRAG: 1 << 9, //          level: lethal held (+frag, throws hatchet)
  SWITCH_WEAPON: 1 << 10, // level: weapnext held. Pmove switches on the press edge (next owned primary).
  HOLD_BREATH: 1 << 11, //  level
  TACTICAL: 1 << 12, //     level: +smoke (unused in SnS, no tactical)
  GOSTAND: 1 << 13, //      edge helper: "stand up" request when jump pressed while crouched/prone
  LEAN_LEFT: 1 << 14, //    unused in BO2 MP
  LEAN_RIGHT: 1 << 15, //   unused in BO2 MP
  SCOREBOARD: 1 << 16, //   cosmetic (server may show others who is looking)
} as const;

export interface UserCmd {
  /** Command number, consecutive per connection, starts at 0. u32. */
  number: number;
  /**
   * Command-timeline time, integer ms: cmdTimeBase + floor(number * 1000 / 60) (constants.CMD_HZ).
   * msec for pmove = serverTime - ps.commandTime (16/17 alternating). NOT the antilag time.
   */
  serverTime: number;
  /** Button bits. */
  buttons: number;
  /** Absolute view angles as shorts (ANGLE2SHORT), [pitch, yaw, roll]; viewangles = cmd + ps.deltaAngles. */
  angles: [number, number, number];
  /** -127..127 (forward positive). Analog stick magnitude survives (CmdScale) [PM §5]. */
  forwardmove: number;
  /** -127..127 (right positive). */
  rightmove: number;
  /** Requested weapon (input sets this; pmove's weapon code starts the switch). */
  weapon: WeaponId;
  /**
   * Antilag time: server-timeline ms the client was RENDERING remote entities at when this cmd
   * was generated (floor(renderTime)) [NET §7.1]. Server sanitizes it (clampViewTime).
   */
  viewTime: number;
  /** Auto-melee assist output [CTL §5.5]: lunge yaw (short) and distance (0..255 units, 0 = none). */
  meleeChargeYaw: number;
  meleeChargeDist: number;
}

export function createUserCmd(): UserCmd {
  return {
    number: 0, serverTime: 0, buttons: 0, angles: [0, 0, 0], forwardmove: 0, rightmove: 0,
    weapon: WeaponId.NONE, viewTime: 0, meleeChargeYaw: 0, meleeChargeDist: 0,
  };
}
export function copyUserCmd(dst: UserCmd, src: UserCmd): UserCmd {
  dst.number = src.number; dst.serverTime = src.serverTime; dst.buttons = src.buttons;
  dst.angles[0] = src.angles[0]; dst.angles[1] = src.angles[1]; dst.angles[2] = src.angles[2];
  dst.forwardmove = src.forwardmove; dst.rightmove = src.rightmove; dst.weapon = src.weapon;
  dst.viewTime = src.viewTime; dst.meleeChargeYaw = src.meleeChargeYaw; dst.meleeChargeDist = src.meleeChargeDist;
  return dst;
}

// =============================================================================================
// PlayerState — everything pmove + the weapon state machine read or write (prediction-critical)
// =============================================================================================
/** Size of the predictable event ring (Q3 MAX_PS_EVENTS 2; BO2 predictableEventParms[3]+; we use 4). */
export const MAX_PS_EVENTS = 4;

/**
 * The local player's authoritative state. Server and client prediction run the SAME pmove on it.
 * RULE [NET §6.3, pitfall 3]: every field here is networked (shared/playerstate.ts PS_SCHEMA);
 * a field pmove touches that is not in here = guaranteed misprediction. Floats are kept at f32
 * (froundPlayerState at the end of every pmove). Offsets in comments are t6mp playerState_t
 * offsets from research/pmove-t6mp.md §1 where known.
 */
export interface PlayerState {
  // ---- identity / timing -----------------------------------------------------------------
  clientNum: number; //            u8
  /** cmd.serverTime of the last executed cmd (0x00). */
  commandTime: number; //          i32
  pm_type: PmType; //              u8   (0x04)
  pm_flags: number; //             u32  PMF_* (0x0c)
  /** Generic movement timer, ms (land slowdown etc.) (0x1c). */
  pm_time: number; //              i32
  eFlags: number; //               u32  EF_*
  /** Increments on every spawn/teleport: no interpolation/smoothing across a change. */
  spawnCount: number; //           u8
  /** Buttons of the previous cmd (replaces pm->oldcmd so a replay needs no extra state). */
  oldButtons: number; //           u32

  // ---- movement ----------------------------------------------------------------------------
  origin: Vec3; //                 f32x3 (0x28)
  /** Integers after every PmoveSingle (truncate toward 0, PM §2). (0x34) */
  velocity: Vec3; //               f32x3
  /** Final view angles in degrees (cmd.angles + deltaAngles, pitch clamped). */
  viewangles: Vec3; //             f32x3
  /** Shorts added to cmd.angles; server sets these to force a view direction (spawn). */
  deltaAngles: [number, number, number]; // i16x3
  gravity: number; //              i32 (0x8c) from G_GRAVITY
  speed: number; //                i32 (0x94) from G_SPEED
  /** Script setMoveSpeedScale (0x50c). SnS sets 1. */
  moveSpeedScaleMultiplier: number; // f32
  /** Lean fraction (0x90); always 0 in BO2 MP. */
  leanf: number; //                f32
  /** Entity number of the ground, ENTITYNUM_NONE in air (0xa4). Ground plane is recomputed each PmoveSingle. */
  groundEntityNum: number; //      u16
  /** Ladder normal (0xb4). */
  vLadderVec: Vec3; //             f32x3
  /** cmd.serverTime of the last jump (0xc0). */
  jumpTime: number; //             i32
  /** origin.z at take-off (0xc4). */
  jumpOriginZ: number; //          f32
  /** Damage slowdown timer, ms counting down (0xe4). */
  damageTimer: number; //          i32
  /** 0..3 (0x5b8). */
  waterlevel: number; //           u8
  /** IW/T5 PM_SetMovementDir: move yaw relative to the view, -90..90 deg (backwards runs mirrored), as i8 in a u8
   *  (read with `(v << 24) >> 24`); 0 when not moving. Third-person leg anims (pmove #P1). */
  movementDir: number; //          u8
  /** Footstep/bob cycle 0..255 (0x08). */
  bobCycle: number; //             u8
  /** 0..255 spread scale (0x588); jump adds 64. */
  aimSpreadScale: number; //       f32

  // ---- stance / view height (0x204..0x214) -------------------------------------------------
  /** 11 prone, 40 crouch, VIEWHEIGHT_STAND standing. */
  viewHeightTarget: number; //     i32
  viewHeightCurrent: number; //    f32
  viewHeightLerpTime: number; //   i32 (cmd time the prone/unprone transition started, 0 = none)
  viewHeightLerpTarget: number; // i32
  viewHeightLerpDown: number; //   u8 (bool)
  /** Prone body orientation (turns at 70 deg/s, PM §2 0x64c9b0). */
  proneDirection: number; //       f32
  proneDirectionPitch: number; //  f32
  proneTorsoPitch: number; //      f32

  // ---- sprint (0x4b8..0x4d0) ---------------------------------------------------------------
  sprintButtonUpRequired: number; // u8 (bool)
  sprintDelay: number; //          u8 (bool) "ran out" flag
  lastSprintStart: number; //      i32 ms
  lastSprintEnd: number; //        i32 ms
  sprintStartMaxLength: number; // i32 ms
  /** Server-set sprint base, ms (SnS: SNS_SPRINT_DURATION_MS). */
  sprintDurationBase: number; //   i32
  /** Server-set sprint cooldown, ms (SnS: 0). */
  sprintCooldown: number; //       i32

  // ---- mantle (IW mantleState_t) -----------------------------------------------------------
  mantleYaw: number; //            f32
  mantleTimer: number; //          i32
  mantleTransIndex: number; //     u8
  mantleFlags: number; //          u8

  // ---- dive to prone (dtp_* dvars) — layout ESTIMATE, pmove agent may request changes -------
  dtpState: number; //             u8
  dtpTime: number; //              i32
  dtpYaw: number; //               f32

  // ---- melee lunge (T5 ps fields) ----------------------------------------------------------
  meleeChargeYaw: number; //       f32
  meleeChargeDist: number; //      u8
  meleeChargeTime: number; //      i32

  // ---- weapons -----------------------------------------------------------------------------
  /** Held weapon (0x1b8). Stays the primary while an offhand is thrown (IW). */
  weapon: WeaponId; //             u8
  weaponState: WeaponState; //     u8
  /** Time left in the current weapon state, ms. */
  weaponTime: number; //           i32
  /** Delay before the state's action (fireDelay, meleeDelay...), ms. */
  weaponDelay: number; //          i32
  /** Viewmodel anim: WeapAnim | WEAPANIM_TOGGLEBIT. */
  weaponAnim: number; //           u16
  /** Weapon to raise after a switch (pending switch target), NONE if none. */
  weaponPending: WeaponId; //      u8
  /** Offhand being used (hatchet) while in OFFHAND_* states, else NONE. */
  offHandIndex: WeaponId; //       u8
  /** Lethal slot weapon (setoffhandprimaryclass): HATCHET in SnS. */
  offhandPrimary: WeaponId; //     u8
  /** ps.meleeWeapon: KNIFE in SnS (used when the held weapon has useAsMelee 0). */
  meleeWeapon: WeaponId; //        u8
  /** Cook timer for grenades, ms (hatchet cannot cook; kept for completeness). */
  grenadeTimeLeft: number; //      i32
  /** Bitmask of owned weapons (1 << WeaponId). */
  weaponsOwned: number; //         u32
  /** Rounds in the clip, indexed by WeaponId. */
  ammoClip: number[]; //           u8 x NUM_WEAPONS
  /** Reserve rounds, indexed by WeaponId. */
  ammoStock: number[]; //          u8 x NUM_WEAPONS
  /** ADS fraction 0 (hip) .. 1 (ADS) (T5 fWeaponPosFrac). */
  adsFrac: number; //              f32
  /** T5 adsDelayTime, ms. */
  adsDelayTime: number; //         i32
  /** Per-life fire counter: tags missiles so the client matches predicted ones (NET §8.3). */
  fireSeq: number; //              u16
  /**
   * (weapons, CONTRACT-REQUEST #W1) T5 ps.weaponShotCount: shots fired since the trigger was last released.
   * A single-shot weapon (crossbow, ballistic knife) needs a re-press: ShotLimitReached [T5 bg_weapons.cpp].
   */
  weaponShotCount: number; //      u8
  /**
   * (weapons, #W1) Bitmask (1 << WeaponId) of weapons already raised this life: T5 PlayerHeldWeapon.heldBefore.
   * A weapon not in the mask plays its first raise (firstRaiseTime) [T5 PM_Weapon_FinishWeaponChange].
   */
  weaponsHeldBefore: number; //    u8
  /**
   * (weapons, #W1) ps.aimSpreadScale (0..255) at the moment of the last shot, BEFORE that shot's fireAdd.
   * The bolt's spread is computed from it (T5 FireWeapon reads the previous frame's currentAimSpreadScale).
   */
  fireSpreadScale: number; //      u8

  // ---- health (server-authoritative, networked for HUD; never predicted) -------------------
  health: number; //               i16
  maxHealth: number; //            u8

  // ---- predictable events (Q3 ps.events ring) ----------------------------------------------
  /** Increments per event; event i lives in slot (seq & (MAX_PS_EVENTS-1)). */
  eventSequence: number; //        u16
  events: number[]; //             u8 x MAX_PS_EVENTS  (PredictableEvent)
  eventParms: number[]; //         u16 x MAX_PS_EVENTS
}

// =============================================================================================
// Events
// =============================================================================================
/**
 * Predictable events: produced INSIDE pmove / the weapon state machine and stored in the
 * PlayerState event ring (shared/playerstate.ts addPredictableEvent). The local client plays
 * them immediately when predicting and dedupes by eventSequence; the server forwards them to
 * other clients as GameEventType.ENT_EVENT. The server also reacts to some (FIRE -> spawn missile,
 * MELEE_STRIKE -> melee trace, OFFHAND_THROW -> spawn hatchet). u8 on the wire, append-only.
 */
export const PEv = {
  NONE: 0,
  FOOTSTEP_WALK: 1, //      parm = surfaceType
  FOOTSTEP_RUN: 2, //       parm = surfaceType
  FOOTSTEP_SPRINT: 3, //    parm = surfaceType
  FOOTSTEP_CROUCH: 4, //    parm = surfaceType
  FOOTSTEP_PRONE: 5, //     parm = surfaceType
  JUMP: 6, //               parm = surfaceType
  LAND: 7, //               parm = surfaceType | (severity << 8) | (viewDip << 10)  severity 0 soft, 1 medium, 2 hard,
  //                        3 damage landing (see FALL_DAMAGE); viewDip 0..24 = PM_CrashLand landing dip (pmove #P1)
  STEP: 8, //               parm = |dz| in bits 0..14, bit 15 set = step DOWN (view smoothing) (pmove #P1)
  STANCE: 9, //             parm = new Stance
  SPRINT_START: 10,
  SPRINT_END: 11,
  MANTLE: 12, //            parm = mantle transition 0..6 (57,51,45,39,33,27,21 u) | 0x10 if it is a mantle OVER (pmove #P1)
  DIVE_START: 13,
  DIVE_LAND: 14, //         parm = surfaceType
  WEAPON_FIRE: 15, //       parm = WeaponId.  SERVER: spawns the missile on this event.
  WEAPON_FIRE_LAST: 16, //  parm = WeaponId (last round in clip)
  WEAPON_DRYFIRE: 17, //    parm = WeaponId
  RELOAD_START: 18, //      parm = WeaponId
  RELOAD_ADD_AMMO: 19, //   parm = WeaponId (reloadAddTime reached; ammo credited)
  RELOAD_END: 20, //        parm = WeaponId
  WEAPON_RAISE: 21, //      parm = WeaponId | (kind << 8) kind 0 normal 1 first 2 quick 3 empty
  WEAPON_DROP: 22, //       parm = WeaponId | (kind << 8)
  ADS_UP: 23, //            parm = WeaponId
  ADS_DOWN: 24, //          parm = WeaponId
  MELEE_SWING: 25, //       parm = melee WeaponId (swing starts)
  MELEE_LUNGE: 26, //       parm = melee WeaponId (charge swing starts)
  MELEE_STRIKE: 27, //      parm = melee WeaponId. SERVER: does the (rewound) melee trace now.
  OFFHAND_START: 28, //     parm = WeaponId (hatchet raised)
  OFFHAND_THROW: 29, //     parm = WeaponId. SERVER: spawns the hatchet.
  OFFHAND_END: 30,
  NOAMMO: 31, //            parm = WeaponId
  WEAPON_SWITCH: 32, //     parm = new WeaponId
  FALL_DAMAGE: 33, //       parm = damage 1..100 (PM_CrashLand). SERVER: applies it, MOD_FALLING (pmove #P1)
} as const;
export type PEv = (typeof PEv)[keyof typeof PEv];

/** Means of death (BO2 MOD_* strings, numeric here; u8 on the wire). */
export const MeansOfDeath = {
  UNKNOWN: 0,
  PISTOL_BULLET: 1, //   obituary relabel for crossbow/b-knife non-headshot deaths (_globallogic_player.gsc:2459)
  RIFLE_BULLET: 2,
  GRENADE: 3,
  GRENADE_SPLASH: 4, //  explosive bolt blast (ESTIMATE: grenade-type weapon splash)
  PROJECTILE: 5,
  PROJECTILE_SPLASH: 6,
  MELEE: 7, //           knife / ballistic-knife stab / crossbow bash -> +5 points
  HEAD_SHOT: 8, //       ballistic knife MOD_IMPACT counts as headshot only for knife_ballistic_mp
  CRUSH: 9,
  FALLING: 10,
  SUICIDE: 11,
  TRIGGER_HURT: 12, //   Hijacked ocean / out of bounds (self-bankrupt)
  EXPLOSIVE: 13,
  IMPACT: 14, //         bolt/knife/axe direct hit
  DROWN: 15,
} as const;
export type MeansOfDeath = (typeof MeansOfDeath)[keyof typeof MeansOfDeath];

/** Server-originated events carried in snapshots [NET §6.5]. u8 on the wire, append-only. */
export const GameEventType = {
  ENT_EVENT: 1, //       another player's predictable event, replayed on the render timeline
  KILL: 2, //            obituary / kill feed (+ bankrupt flag)
  DAMAGE_TAKEN: 3, //    to the victim only: hit direction indicator + rumble
  HITMARKER: 4, //       to the attacker only (server-confirmed)
  EXPLOSION: 5, //       explosive bolt detonation
  IMPACT: 6, //          missile hit the world (bolt thunk / knife thunk) - surface sounds + fx
  MISSILE_STICK: 7, //   bolt/knife/axe stuck in world or player
  MISSILE_BOUNCE: 8, //  hatchet bounce (wpn_hatchet_bounce_<surface>)
  MELEE_HIT: 9, //       server-confirmed melee hit (blood fx, wpn_melee_knife_hit_body)
  PAIN: 10,
  DEATH: 11, //          death anim/sound for an entity
  SPAWN: 12, //          player (re)spawned (also resets interpolation via spawnCount)
  PICKUP: 13, //         picked up a knife/axe
  SCORE: 14, //          scoreboard row update
  SCORE_POPUP: 15, //    "+10" popup for the local player
  MEDAL: 16, //          "Bankrupted", "Regicide", "Switch Hitter", ...
  BANKRUPT: 17, //       victim bankrupted (HUMILIATED / BANKRUPTED splash + mpl_wager_bankrupt)
  ANNOUNCER: 18, //      leader dialog alias for one player (vox_wm_*)
  SOUND: 19, //          generic positional or local sound alias
  MATCH: 20, //          match state change (prematch/playing/postgame) + time left
  SPLASH: 21, //         water splash death (mpl_splash_death + fx)
  TOP3: 22, //           entered/left "in the money"
  KILLCAM: 23, //        (gametype) a killcam starts for this client: replay attacker's view [SAS §8, §9]
} as const;
export type GameEventType = (typeof GameEventType)[keyof typeof GameEventType];

export const KillFlags = { HEADSHOT: 1, BANKRUPT: 2, SUICIDE: 4, BOUNCED: 8, STUCK: 16 } as const;

export const Medal = {
  BANKRUPTED: 1, REGICIDE: 2, SWITCH_HITTER: 3, FIRST_BLOOD: 4, BANKSHOT: 5,
  // (gametype, CONTRACT-REQUESTS #G4) general score-event medals with a non-zero "sas XP" in
  // extracted/mp/patch_mp/mp/scoreinfo.csv (row = scoreinfo "Script Reference", art = column 3)
  HEADSHOT: 6, //            headshot                       hud_medals_headshot
  ASSISTED_SUICIDE: 7, //    assisted_suicide               hud_medals_assist_suicide
  REVENGE: 8, //             revenge_kill ("Payback")       hud_medals_revenge
  SKEWERED: 9, //            ballistic_knife_kill           hud_medals_skewer
  LONGSHOT: 10, //           longshot_kill                  hud_medals_longshot
  BACKSTABBER: 11, //        backstabber_kill               hud_medals_backstabber
  CLOSE_CALL: 12, //         kill_enemy_when_injured        hud_medals_closecall
  AFTERLIFE: 13, //          kill_enemy_after_death         hud_medals_afterlife
  COMEBACK: 14, //           comeback_from_deathstreak      hud_medals_comeback
  DOUBLE_KILL: 15, //        multikill_2                    hud_medals_doublekill
  TRIPLE_KILL: 16, //        multikill_3                    hud_medals_triplekill
  QUAD_KILL: 17, //          multikill_4                    hud_medals_quadkill
  FRENZY_KILL: 18, //        multikill_5                    hud_medals_frenzykill
  SUPER_KILL: 19, //         multikill_6                    hud_medals_superkill
  MEGA_KILL: 20, //          multikill_7                    hud_medals_megakill
  ULTRA_KILL: 21, //         multikill_8                    hud_medals_ultrakill
  KILL_CHAIN: 22, //         multikill_more_than_8          hud_medals_killchain
  UNINTERRUPTED_OBIT: 23, // uninterrupted_obit_feed_kills  hud_medals_fourpiece
} as const;
export type Medal = (typeof Medal)[keyof typeof Medal];

/** Match phase [SAS §1, §9]. */
export const MatchPhase = { WAITING: 0, PREMATCH: 1, PLAYING: 2, POSTGAME: 3, FINAL_KILLCAM: 4, INTERMISSION: 5 } as const;
export type MatchPhase = (typeof MatchPhase)[keyof typeof MatchPhase];

interface EvBase {
  /** Server time ms when it happened (the client plays it when renderTime reaches it). */
  time: number;
}
export type GameEvent =
  | (EvBase & { type: typeof GameEventType.ENT_EVENT; ent: number; ev: PEv; parm: number; seq: number })
  | (EvBase & { type: typeof GameEventType.KILL; killer: number; victim: number; weapon: WeaponId; mod: MeansOfDeath; flags: number })
  | (EvBase & { type: typeof GameEventType.DAMAGE_TAKEN; attacker: number; amount: number; dirYaw: number })
  | (EvBase & { type: typeof GameEventType.HITMARKER; victim: number; flags: number })
  | (EvBase & { type: typeof GameEventType.EXPLOSION; pos: Vec3; weapon: WeaponId; owner: number })
  | (EvBase & { type: typeof GameEventType.IMPACT; pos: Vec3; normal: Vec3; surface: number; weapon: WeaponId })
  | (EvBase & { type: typeof GameEventType.MISSILE_STICK; entId: number; pos: Vec3; normal: Vec3; surface: number; weapon: WeaponId; stuckTo: number })
  | (EvBase & { type: typeof GameEventType.MISSILE_BOUNCE; entId: number; pos: Vec3; surface: number; weapon: WeaponId })
  | (EvBase & { type: typeof GameEventType.MELEE_HIT; attacker: number; victim: number; weapon: WeaponId })
  | (EvBase & { type: typeof GameEventType.PAIN; ent: number; health: number })
  | (EvBase & { type: typeof GameEventType.DEATH; ent: number; mod: MeansOfDeath; weapon: WeaponId; dirYaw: number })
  | (EvBase & { type: typeof GameEventType.SPAWN; ent: number })
  | (EvBase & { type: typeof GameEventType.PICKUP; ent: number; weapon: WeaponId })
  | (EvBase & { type: typeof GameEventType.SCORE; ent: number; points: number; kills: number; deaths: number; tomahawks: number; humiliated: number })
  | (EvBase & { type: typeof GameEventType.SCORE_POPUP; amount: number })
  | (EvBase & { type: typeof GameEventType.MEDAL; medal: Medal })
  | (EvBase & { type: typeof GameEventType.BANKRUPT; victim: number; attacker: number })
  | (EvBase & { type: typeof GameEventType.ANNOUNCER; alias: string })
  | (EvBase & { type: typeof GameEventType.SOUND; alias: string; pos: Vec3 | null; ent: number })
  | (EvBase & { type: typeof GameEventType.MATCH; phase: MatchPhase; timeLeftMs: number })
  | (EvBase & { type: typeof GameEventType.SPLASH; pos: Vec3 })
  | (EvBase & { type: typeof GameEventType.TOP3; entered: boolean })
  /**
   * Killcam timing (gametype; the replay itself is a client/netcode feature). Footage covers server time
   * [deathTime - camMs, deathTime + postMs] from `attacker`'s view, played in real time starting at `time`.
   * final = end-of-match final killcam (slow motion, _killcam.gsc:282-296). A SPAWN of the victim ends it early.
   */
  | (EvBase & { type: typeof GameEventType.KILLCAM; attacker: number; victim: number; weapon: WeaponId; mod: MeansOfDeath; deathTime: number; camMs: number; postMs: number; final: boolean });

// =============================================================================================
// Networked entities (what other clients see) [NET §6.2]
// =============================================================================================
/** Movement flag bits for remote players (third-person anims, HUD). */
export const MoveFlag = {
  ON_GROUND: 1, SPRINTING: 2, ADS: 4, MANTLING: 8, MELEE_LUNGE: 16, DEAD: 32, BACKWARDS: 64, DIVING: 128,
} as const;

export interface PlayerEntityState {
  clientNum: number;
  spawnCount: number;
  origin: Vec3;
  /** [pitch, yaw, roll] degrees (view angles; body yaw is derived by the anim system). */
  angles: Vec3;
  velocity: Vec3;
  stance: Stance;
  /** View height (eye z offset) for hit boxes and third-person head look. */
  viewHeight: number;
  moveFlags: number;
  eFlags: number;
  weapon: WeaponId;
  weaponState: WeaponState;
  /** Third-person anim ids incl. toggle bit (player_anims.json index). */
  legsAnim: number;
  torsoAnim: number;
  /** Event sequence of this player's predictable events (dedupe on the owning client). */
  eventSeq: number;
}

export const MissileKind = { BOLT: 1, BALLISTIC_KNIFE: 2, HATCHET: 3 } as const;
export type MissileKind = (typeof MissileKind)[keyof typeof MissileKind];
/** 0 flying, 1 stuck in world, 2 stuck in player, 3 pickup lying around (knife/axe). */
export const MissileState = { FLYING: 0, STUCK_WORLD: 1, STUCK_PLAYER: 2, PICKUP: 3 } as const;
export type MissileState = (typeof MissileState)[keyof typeof MissileState];

export interface MissileEntityState {
  entId: number;
  kind: MissileKind;
  owner: number;
  /** Owner's ps.fireSeq at launch: lets the owner hide its server copy behind its predicted one. */
  fireSeq: number;
  state: MissileState;
  /** clientNum when state == STUCK_PLAYER, else 0xff. */
  parent: number;
  /** World position (states 0,1,3) or local offset in the parent's frame (state 2). */
  origin: Vec3;
  velocity: Vec3;
  angles: Vec3;
  /** Explosive bolt: server time ms of detonation (0 = not armed). Drives beep/blink timing client side. */
  explodeAt: number;
  /** Server time ms the missile was fired. */
  spawnTime: number;
}

/** Static per-player info from the lobby (control channel), not in snapshots. */
export interface PlayerInfo {
  clientNum: number;
  name: string;
  isBot: boolean;
  /** 0 = SEALs (allies body), 1 = PMC (axis body); random per match [WPN §6]. */
  body: number;
  ping: number;
  ready: boolean;
}

/** One decoded server snapshot (the codec is netcode's; this is the decoded shape). */
export interface Snapshot {
  tick: number;
  serverTime: number;
  /** Last cmd number executed for THIS client: prediction replays cmds after it. */
  lastExecutedCmd: number;
  /** Bit 0 matchFrozen, bit 1 intermission, bit 2 killcam. */
  flags: number;
  ps: PlayerState;
  players: PlayerEntityState[];
  missiles: MissileEntityState[];
  events: GameEvent[];
}

/** Scoreboard row [SAS §10: pointstowin, kills, deaths, tomahawks, humiliated]. */
export interface ScoreRow {
  clientNum: number;
  points: number;
  kills: number;
  deaths: number;
  tomahawks: number;
  humiliated: number;
  ping: number;
}
